import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useCdsSourceSettingsText,
  useCdsSourceSettingsViewModel,
} from "@onetasklist/shared/sources/cds";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface CdsSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const CdsSourceSettings: React.FC<CdsSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useCdsSourceSettingsText(t);
  const vm = useCdsSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [entityQuery, setEntityQuery] = useState("");
  const [statusesQuery, setStatusesQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");
  const [dueDateQuery, setDueDateQuery] = useState("");
  const [assignedToQuery, setAssignedToQuery] = useState("");
  const [tagsQuery, setTagsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      {!vm.isAuthorisedProfileReady && (
        <Text>{text.unauthorisedWarningText}</Text>
      )}

      {vm.isAuthorisedProfileReady && (
        <SettingsSectionCard
          sectionOffset={sectionOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.cds.sectionDataSource",
          )}
        >
          {vm.environmentDisplayName && vm.environmentUrl && (
            <Field orientation="horizontal" label={text.environmentLabelText}>
              <a href={vm.environmentUrl} target="_blank" rel="noreferrer">
                {vm.environmentDisplayName}
              </a>
            </Field>
          )}

          <Field
            orientation="horizontal"
            label={text.dataToLoadLabelText}
            hint={text.dataToLoadHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedEntityDefinitionId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedEntityDefinitionId
              }
              selectedLabel={
                vm.entityDefinitions.find(
                  (entity) =>
                    entity.metadataId === vm.selectedEntityDefinitionId,
                )?.collectionLabel ?? text.dataToLoadPlaceholderText
              }
              query={entityQuery}
              placeholder={text.dataToLoadPlaceholderText}
              options={vm.entityDefinitions.map((entityDefinition) => ({
                id: entityDefinition.metadataId,
                label:
                  entityDefinition.collectionLabel ??
                  entityDefinition.label ??
                  entityDefinition.metadataId,
              }))}
              onQueryChange={setEntityQuery}
              onSelect={vm.onSelectEntityDefinition}
              clearSelectionText={text.dataToLoadPlaceholderText}
              isLoading={!vm.entityDefinitionsLoaded}
            />
          </Field>

          {!vm.entityDefinitionsLoaded && (
            <Text>{text.loadingEntityDefinitionsText}</Text>
          )}
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady &&
        vm.selectedEntityDefinitionId !== vm.noneOptionValue && (
          <SettingsSectionCard
            sectionOffset={sectionOffset + 1}
            title={t(
              "board.connections.catalogue.sourceSettings.cds.sectionFields",
            )}
          >
            <Field
              orientation="horizontal"
              label={text.statusesLabelText}
              hint={text.statusesHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedStatusValues}
                query={statusesQuery}
                placeholder={text.statusesPlaceholderText}
                options={vm.statuses.map((status) => ({
                  id: status.value.toString(),
                  label: status.label ?? status.value.toString(),
                }))}
                onQueryChange={setStatusesQuery}
                onSelectionChange={vm.onSelectStatuses}
                minQueryLength={0}
                typeToSearchText={text.statusesHintText}
                isLoading={!vm.statusesLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.doneStatusLabelText}
              hint={text.doneStatusHintText}
              validationState={vm.isDoneStatusInactive ? "warning" : "none"}
              validationMessage={
                vm.isDoneStatusInactive
                  ? text.doneStatusInactiveWarningText
                  : undefined
              }
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedDoneStatusValue === vm.noneOptionValue
                    ? undefined
                    : vm.selectedDoneStatusValue
                }
                selectedLabel={
                  vm.statuses.find(
                    (status) =>
                      status.value.toString() === vm.selectedDoneStatusValue,
                  )?.label
                }
                query={doneStatusQuery}
                placeholder={text.doneStatusPlaceholderText}
                options={vm.statuses.map((status) => ({
                  id: status.value.toString(),
                  label: status.label ?? status.value.toString(),
                }))}
                onQueryChange={setDoneStatusQuery}
                onSelect={vm.onSelectDoneStatus}
                clearSelectionText={text.doneStatusPlaceholderText}
                isLoading={!vm.statusesLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.dueDateFieldLabelText}
              hint={text.dueDateFieldHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedDueDateAttributeId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedDueDateAttributeId
                }
                selectedLabel={
                  vm.dateAttributes.find(
                    (attribute) =>
                      attribute.metadataId === vm.selectedDueDateAttributeId,
                  )?.label
                }
                query={dueDateQuery}
                placeholder={text.dueDateFieldPlaceholderText}
                options={vm.dateAttributes.map((attribute) => ({
                  id: attribute.metadataId,
                  label:
                    attribute.label ??
                    attribute.schemaName ??
                    attribute.metadataId,
                }))}
                onQueryChange={setDueDateQuery}
                onSelect={vm.onSelectDueDateAttribute}
                clearSelectionText={text.dueDateFieldPlaceholderText}
                isLoading={!vm.dateAttributesLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.assignedToFieldLabelText}
              hint={text.assignedToFieldHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedAssignedToAttributeId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedAssignedToAttributeId
                }
                selectedLabel={
                  vm.assignedToAttributes.find(
                    (attribute) =>
                      attribute.metadataId === vm.selectedAssignedToAttributeId,
                  )?.label
                }
                query={assignedToQuery}
                placeholder={text.assignedToFieldPlaceholderText}
                options={vm.assignedToAttributes.map((attribute) => ({
                  id: attribute.metadataId,
                  label:
                    attribute.label ??
                    attribute.schemaName ??
                    attribute.metadataId,
                }))}
                onQueryChange={setAssignedToQuery}
                onSelect={vm.onSelectAssignedToAttribute}
                clearSelectionText={text.assignedToFieldPlaceholderText}
                isLoading={!vm.assignedToAttributesLoaded}
              />
            </Field>

            <Field orientation="horizontal" label={text.tagsFieldLabelText}>
              <SourceMultiTagPicker
                selectedIds={vm.selectedTagAttributeIds}
                query={tagsQuery}
                placeholder={text.tagsFieldPlaceholderText}
                options={vm.attributes.map((attribute) => ({
                  id: attribute.metadataId,
                  label:
                    attribute.label ??
                    attribute.schemaName ??
                    attribute.metadataId,
                }))}
                onQueryChange={setTagsQuery}
                onSelectionChange={vm.onSelectTagAttributes}
                minQueryLength={0}
                typeToSearchText={text.tagsFieldPlaceholderText}
                isLoading={!vm.attributesLoaded}
              />
            </Field>

            <Field orientation="horizontal" label={text.assignedToMeLabelText}>
              <Checkbox checked disabled />
            </Field>

            {!vm.statusesLoaded && <Text>{text.loadingStatusesText}</Text>}
            {!vm.dateAttributesLoaded && (
              <Text>{text.loadingDateAttributesText}</Text>
            )}
            {!vm.assignedToAttributesLoaded && (
              <Text>{text.loadingAssignedToAttributesText}</Text>
            )}
            {!vm.attributesLoaded && <Text>{text.loadingAttributesText}</Text>}
            {vm.hasError && <Text>{text.loadErrorText}</Text>}
          </SettingsSectionCard>
        )}

      {vm.selectedStatusValues.length > 0 && (
        <StatusMappingSection
          sectionNumber={sectionOffset + 1}
          items={vm.statuses
            .filter((s) => vm.selectedStatusValues.includes(s.value.toString()))
            .map((s) => ({
              id: s.value.toString(),
              name: s.label || s.value.toString(),
            }))}
          mappings={
            ((editingConnection.configuration as Record<string, unknown>)
              ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
          }
          boardStatuses={boardStatuses}
          onChange={(mappings) =>
            onUpdate({
              configuration: {
                ...(editingConnection.configuration as Record<string, unknown>),
                statusMappings: mappings,
              },
            })
          }
        />
      )}
    </>
  );
};
